import { BCMSRecord, DisabledPrompt, Link, useWorkflowStep } from '@bcm-dev/bcms-vite';
import { getDefaultEdit, getWorkflowSteps } from 'compid/workflows/EditBillerWorkflow';
import { Bill, Biller } from 'model';

import ButtonContainer from 'compid/shared/containers/ButtonContainer';
import PageLayoutContainer from 'compid/shared/containers/PageLayoutContainer';
import CenterColumnContainer from 'compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer';
import PageView from 'compid/shared/containers/PageViewContainer';
import BillerView from 'compid/shared/views/BillerView';
import UIButton from 'compid/ui/UIButton';
import UIEditTextInput from 'compid/ui/UIEditTextInput';
import React from 'react';

import UIEditAccountNumberInput from 'compid/ui/UIEditAccountNumberInput';
import UIEditMailAddressInput from 'compid/ui/UIEditMailAddressInput';
import UIEditPhoneInput from 'compid/ui/UIEditPhoneInput';
import './styles.css';

/**
 * @function EditBillerDetailsPage
 * @returns {React.Component}
 * @description The main component for the edit biller details page
 * @example
 * <EditBillerDetailsPage />
 */
export default function EditBillerDetailsPage(props) {
  // Initalize the workflow step and global controllers for the model classes used
  const workflowStep = useWorkflowStep(getDefaultEdit, getWorkflowSteps);
  BCMSRecord.globalControllerFor([ Biller, Bill ], workflowStep.globalController);
  // Set the biller and defaultBiller variables
  const biller = workflowStep.read("biller");
  const isPaper = biller.paymentType === "paper";
  const isElectronic = biller.paymentType === "electronic";
  const isVirtualCard = biller.paymentType === "virtualCard";
  const defaultBiller = Biller.initByID(biller.id);
  const hasEbills = biller.eBillStatus === "Active";
  const update = prop => value => workflowStep.update([ "biller", prop ], value);

  const cancel = evt => workflowStep.navigate(`/billerDetails/details/${ biller.id }`);
  const save = evt => {
    // Update the biller in the global model
    BCMSRecord.batch([
      Biller.update(biller)
    ], workflowStep.globalController);

    // Navigate back to the biller details page
    workflowStep.navigate(`/billerDetails/details/${ biller.id }`,
       { "state" : { "confirmation" : { "edited" : true, "biller" : biller.record, "billerOld" : defaultBiller.record } } });
  };

  return(
    <PageView className="edit-biller-details-page">
      <p className="page-sub-header semi-bold-text">EDIT BILLER</p>

      <PageLayoutContainer>
        <div className="flex-container flex-vertical flex-width">
          <div className="flex-height flex-container flex-vertical upper-content-container">
              {/* BillerView needs hook up - Has to comment it out as it caused issues on the page  */}
              <BillerView biller={ defaultBiller } />
          </div>

          <div className="flex-height flex-width flex-container all-center">
            <CenterColumnContainer>
              <div className="form-group flex-container flex-vertical">
                <b>Account information</b>
                { isPaper && 
                <>
                  <UIEditTextInput label="Biller name" isEditing="false" allowEditing="true" placeHolder="Biller name" value={ biller.name } onChange={ update("name") } />
                  <UIEditAccountNumberInput label="Account number" isEditing="false" allowEditing="true" placeHolder="Account number" value={ biller.number } onChange={ update("number") } />
                </>
                }

                { (isElectronic || isVirtualCard) && 
                <UIEditAccountNumberInput label="Account number" isEditing="false" allowEditing={ hasEbills ? "false" : "true" } placeHolder="Account number" value={ biller.number } onChange={ update("number") } />
                }

                <UIEditTextInput label="Nickname" isEditing="false" allowEditing="true" placeHolder="Nickname" value={ biller.nickname } onChange={ update("nickname") } />
              
                { isPaper &&
                <>
                  <UIEditMailAddressInput label="Mailing address" isEditing="false" allowEditing="true"
                  placeHolder="Mailing address" biller={ biller } onChange={ update } />

                  <UIEditPhoneInput label="Phone number" placeHolder="Biller phone number" value={ biller.phone } 
                  isEditing="false" allowEditing="true" onChange={ update("phone") } />
                </>
                }

                { (isElectronic || isVirtualCard) && 
                <>
                  <UIEditMailAddressInput label="Mailing address" isEditing="false" allowEditing={ hasEbills ? "false" : "true" }
                  placeHolder="Mailing address" biller={ biller } onChange={ update } />

                  <UIEditPhoneInput label="Phone number" placeHolder="Biller phone number" value={ biller.phone } 
                  isEditing="false" allowEditing={ hasEbills ? "false" : "true" } onChange={ update("phone") } />
                </>
                }

                <ButtonContainer className="form-button-container">
                  <UIButton className="secondary-button" onClick={ cancel }>Cancel</UIButton>
                  <UIButton className="primary-button" onClick={ save }>Save changes</UIButton>
                </ButtonContainer>

                <DisabledPrompt>
                  <Link className="text-center delete-biller-link link-button">Delete biller</Link>
                </DisabledPrompt>
              </div>
            </CenterColumnContainer>
          </div>
        </div>
      </PageLayoutContainer>
    </PageView>
  );
}